import React, { Component } from 'react';
import { Link } from 'react-router';

import api from '../utils/api.js';
import uploadFile from '../utils/upload-file.js';

import PublicModal from '../components/PublicModal.js';
import AvatarForm from '../components/AvatarForm.js';
import ProgressButton from '../components/ProgressButton.js';
import PasswordInput from '../components/PasswordInput.js';
import SteamToken from '../components/SteamToken.js';

import './RegisterPage.css';

class RegisterPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      inviteToken: this.props.params && this.props.params.inviteToken,
      name: '',
      displayName: '',
      email: '',
      password: '',
      confirmPassword: '',
      avatar: null,

      isFetchingInvite: false,
      isInviteCancelled: false,
      isInviteRedeemed: false,
      errorFetchingInvite: null,
      showPasswordStrength: false,
      submitting: false,

      registerError: false,
      registerFinished: false,
      copiedToken: false,
      user: null
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.renderError = this.renderError.bind(this);
    this.renderContents = this.renderContents.bind(this);
    this.copyTokenSuccess = this.copyTokenSuccess.bind(this);

    this.checkValid = this.checkValid.bind(this);
  }

  componentDidMount() {
    const { inviteToken } = this.state;
    this.setState({ isFetchingInvite: true });

    api(`/invites/token/${inviteToken}`)
      .then((invite) => {
        this.setState({
          isFetchingInvite: false,
          email: invite.email,
          isInviteCancelled: invite.cancelled,
          isInviteRedeemed: invite.redeemed
        });
      }).catch((err) => {
        this.setState({
          isFetchingInvite: false,
          errorFetchingInvite: err
        });
      });
  }

  renderError() {
    return <div>
      <h1>This invite has already been used</h1>
      <p>This invite has been redeemed already. If you’re the one who redeemed it, <Link to="/login">log in</Link>! If you’re having trouble accessing your account, <Link to="/forgot-password">reset your password</Link>.</p>
    </div>;
  }

  handleInputChange(event) {
    const { target } = event;
    const { value, name } = target;

    // Update the state key of the input's `name` with the new value.
    this.setState({
      [name]: value
    });
  }

  handleSubmit(ev) {
    ev.preventDefault();

    // Mark the form as being submitted...
    this.setState({
      submitting: true,
      registerError: null
    });

    // Then, grab the form fields from component state.
    const {
      avatar,
      displayName,
      email,
      inviteToken,
      name,
      password
    } = this.state;

    // Build out the user object to register with.
    let user = {
      name,
      displayName,
      email,
      inviteToken,
      password
    };

    // Register the user.
    api('/users', {
      method: 'POST',
      body: JSON.stringify(user)
    }).then((payload) => {

      // Grab the newly created user object and session.
      let registeredUser = payload['user'];
      let token = payload['access_token'];

      if (avatar) {
        // If the user chose an avatar, upload it!
        return uploadFile(avatar, token).then((uploadedAvatar) => {
          // Then, update the newly created user object with their new avatar.
          return api(`/users/${registeredUser.id}`, {
            method: 'PUT',
            body: JSON.stringify({ avatar: uploadedAvatar.id }),
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `JWT ${token}`
            }
          });
        });
      } else {
        // Otherwise, just pass on the newly created user.
        return Promise.resolve(registeredUser);
      }
    }).then((user) => {
      // Finally, finish up the registration flow and save the user object.
      this.setState({
        registerFinished: true,
        user: user
      });
    }).catch((err) => {
      this.setState({
          registerError: err,
          submitting: false
        });
      });
  }

  /**
   * Handle the user choosing a new avatar (or removing their selected
   * avatar). Properly sets the `this.state.avatar` property to the File blob representing
   * the avatar, or `null` if the avatar is being removed.
   *
   * @memberof RegisterPage
   */
  handleChangeAvatar = (avatar) => {
    this.setState({ avatar });
  }

  copyTokenSuccess() {
    this.setState({ copiedToken: true });
  }

  checkValid() {
    const { name, displayName, email, password, confirmPassword } = this.state;
    if (!name || !displayName || !email || !password || !confirmPassword) {
      return false;
    }

    if (password !== confirmPassword) {
      return false;
    }

    if (password.length < 7) {
      return false;
    }

    if (displayName.length < 3) {
      return false;
    }

    return true;
  }

  renderContents() {
    let { params } = this.props;
    let { inviteToken } = params;
    let registerErrorMessage;

    if (this.state.isFetchingInvite) {
      return <div></div>;
    }

    if (!inviteToken || this.state.errorFetchingInvite) {
      return this.renderError();
    }

    if (this.state.isInviteCancelled) {
      return (<div><h1>Oh no</h1><p>This invite has been cancelled :(</p></div>);
    }

    if (this.state.isInviteRedeemed) {
      return this.renderError();
    }

    if (this.state.registerFinished) {
      let copyTokenHelpText = this.state.copiedToken ? <p className="text-help">Copied! Now go ahead and activate it in Steam.</p> : <p className="text-help">Click the code above to copy it to your clipboard.</p>;

      return <div className="col-xl-8 col-xl-push-2 col-lg-10 col-lg-push-1">
        <img className="RegisterPage__installer-box" src="/assets/img/wanda-box.svg" />
        <h1>You're all set, {this.state.displayName}!</h1>
        <p>Feel free to download and run the app below to get started, if you're on your Windows PC. Not at your keyboard? Check your email for a link leading back here.</p>

        <a href="https://normal-chat-updates.s3.amazonaws.com/windows/Setup.exe" target="_blank" className="RegisterPage__download-btn"><span className="RegisterPage__download-btn-title">Setup.exe</span><span className="RegisterPage__download-btn-desc">130MB &bull; Windows 64-bit</span></a>
      </div>;
    }

    if (this.state.registerError) {
      registerErrorMessage = <p>Hmm, we had some trouble trying to process your registration. Try again, and if the issue persists, get in touch.</p>;
    }

    let formValid = this.checkValid();

    return (
      <div>
        <h1>Nice to meet you.</h1>
        <p>We just need a little information from you to get started.</p>
        <form className="form" onSubmit={this.handleSubmit}>
          <div className="row">
            <div className="col-sm-12">
              <AvatarForm avatar={this.state.avatar} onAvatarSelect={this.handleChangeAvatar} />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              <p className="text-help">A user avatar we'll use to identify you to other users. Optional, of course.</p>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-6">
              <input className="form-input form-input--dark form-input--fullwidth"
                name="name"
                type="text"
                placeholder="Name"
                onChange={this.handleInputChange}
                value={this.state.name}
                disabled={this.state.submitting} />

              <p className="text-help">How we refer to you within the app.</p>
            </div>

            <div className="col-sm-6">
              <input className="form-input form-input--dark form-input--fullwidth"
                name="displayName"
                type="text"
                placeholder="Display name"
                onChange={this.handleInputChange}
                value={this.state.displayName}
                disabled={this.state.submitting} />

              <p className="text-help">How we present your name to others. Needs to be at least 3 characters long.</p>
            </div>
          </div>

          <input className="form-input form-input--dark form-input--fullwidth"
            type="email"
            name="email"
            disabled={true}
            placeholder="Email address"
            onChange={this.handleInputChange}
            value={this.state.email} />

          <PasswordInput
            inputClassName="form-input form-input--dark form-input--fullwidth form-input-strength"
            name="password"
            placeholder="Password"
            onChange={this.handleInputChange}
            value={this.state.password}
            disabled={this.state.submitting} />

          <input className="form-input form-input--dark form-input--fullwidth"
            type="password"
            name="confirmPassword"
            placeholder="Confirm password"
            onChange={this.handleInputChange}
            value={this.state.confirmPassword}
            disabled={this.state.submitting} />

          {registerErrorMessage}

          <ProgressButton type="submit"
            buttonStyle="dark"
            formSubmit={true}
            label="Sign up"
            loadingLabel="Signing up..."
            loading={this.state.submitting}
            disabled={!formValid} />
        </form>
      </div>
    );
  }

  render() {
    let pageClasses = ['RegisterPage'];

    if (this.state.registerFinished) {
      pageClasses.push('is-finished');
    }

    return (
      <div className={pageClasses.join(' ')}>
        <PublicModal>
          <div className="col-md-6 col-md-push-3">
            {this.renderContents()}
          </div>
        </PublicModal>
      </div>
    )
  }
}

export default RegisterPage;



// WEBPACK FOOTER //
// ./src/containers/RegisterPage.js